import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'wouter';
import {
  clearOidcLoginAttempt,
  clearOidcAutoLoginBlock,
  hasRecentOidcLoginAttempt,
  setOidcAutoLoginBlock,
} from '@/lib/authNavigationGuards';

// Key for tracking callback processing across React Strict Mode remounts
const CALLBACK_PROCESSED_KEY = 'rugira_callback_processed';

export default function AuthCallback() {
  const { handleRedirectCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const hasRunRef = useRef(false);
  const callbackRef = useRef(handleRedirectCallback);

  useEffect(() => {
    callbackRef.current = handleRedirectCallback;
  }, [handleRedirectCallback]);

  useEffect(() => {
    const handleCallback = async () => {
      // Guard 1: Ref-based guard for same-mount double calls
      if (hasRunRef.current) {
        return;
      }

      // Guard 2: SessionStorage-based guard for React Strict Mode unmount/remount
      // This survives component remounting and prevents double code exchange
      const currentUrl = window.location.href;
      const processedUrl = sessionStorage.getItem(CALLBACK_PROCESSED_KEY);
      if (processedUrl === currentUrl) {
        console.log('[AuthCallback] Already processed this callback URL, skipping');
        return;
      }

      hasRunRef.current = true;
      // Mark as processing before async work to prevent race conditions
      sessionStorage.setItem(CALLBACK_PROCESSED_KEY, currentUrl);

      // Check if user just logged out - if so, don't process the callback
      // This prevents auto-login when Keycloak session is still active
      // Check localStorage first (more persistent), then sessionStorage as fallback
      const LOGOUT_GUARD_KEY = 'rugira_logout_guard';
      const LOGOUT_GUARD_TTL_MS = 30000;
      let isLogoutGuardActive = false;

      const timestamp = localStorage.getItem(LOGOUT_GUARD_KEY);
      if (timestamp) {
        const guardAge = Date.now() - parseInt(timestamp, 10);
        if (guardAge < LOGOUT_GUARD_TTL_MS) {
          isLogoutGuardActive = true;
          console.log('[AuthCallback] Logout guard active in localStorage, age:', guardAge, 'ms');
        }
      }
      if (!isLogoutGuardActive && sessionStorage.getItem(LOGOUT_GUARD_KEY) === '1') {
        isLogoutGuardActive = true;
        console.log('[AuthCallback] Logout guard active in sessionStorage');
      }

      if (isLogoutGuardActive) {
        if (hasRecentOidcLoginAttempt()) {
          console.log('[AuthCallback] Recent OIDC login attempt detected; ignoring stale logout guard.');
          localStorage.removeItem(LOGOUT_GUARD_KEY);
          sessionStorage.removeItem(LOGOUT_GUARD_KEY);
        } else {
          console.log('[AuthCallback] Logout guard detected, redirecting to login instead of processing callback');
          sessionStorage.removeItem(CALLBACK_PROCESSED_KEY);
          setLocation('/login', { replace: true });
          return;
        }
      }

      try {
        console.log('[AuthCallback] Processing OIDC callback...');
        const returnPath = await callbackRef.current();
        console.log('[AuthCallback] Callback successful, redirecting to:', returnPath);
        clearOidcLoginAttempt();
        clearOidcAutoLoginBlock();
        // Clear the processed marker on success - future logins should work
        sessionStorage.removeItem(CALLBACK_PROCESSED_KEY);
        // Redirect to the return path or default to overview
        setLocation(returnPath, { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Auth callback error:', err);
        clearOidcLoginAttempt();
        setOidcAutoLoginBlock('callback_failed');
        // Clear on error too so user can retry
        sessionStorage.removeItem(CALLBACK_PROCESSED_KEY);
        setError('Authentication failed. Please try again.');

        // Redirect to login after a delay
        setTimeout(() => {
          setLocation('/login', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-red-600 mb-4 text-lg font-medium">
            {error}
          </div>
          <div className="text-slate-600">
            Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-4" />
        <div className="text-slate-600">
          Completing authentication...
        </div>
      </div>
    </div>
  );
}
